import { captureMessage, withScope } from "@sentry/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrowRight } from "@/assets/icons/icons";
import OpenArrow from "@/assets/ux/ArrowOpenDoc.svg?react";
import { Button } from "@/components/Interface/Button/Button";
import { TruncatedText } from "@/components/Interface/TruncatedText/TruncatedText";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { TableRow } from "@/components/Layout/Table/TableRow/TableRow";
import { BillListCheckbox } from "@/components/Portal/Documents/BillList/components/BillListCheckbox/BillListCheckbox";
import { BillStatusChip } from "@/components/Portal/Documents/BillList/components/BillStatusChip/BillStatusChip";
import { useHandleError } from "@/hooks/useHandleError";
import { getBillDocument } from "@/services/api";
import { BillFile, DrBill } from "@/services/model";
import { BillStatus } from "@/utils/enums";
import { log } from "@/utils/log";
import { getCurrency, getShortDateString } from "@/utils/translationHelpers";

enum statusToTranslationMapping {
  Mahnung = "reminder",
  fällig = "due",
  storniert = "canceled",
  geschlossen = "paid",
}

export const getValidStatus = (status: string) => {
  if (
    status === BillStatus.reminder ||
    status === BillStatus.overdue ||
    status === BillStatus.canceled ||
    status === BillStatus.closed
  ) {
    return statusToTranslationMapping[status];
  } else {
    withScope((scope) => {
      scope.setTag("section", "portal");
      scope.setTag("domain", "documents");
      scope.setContext("status", { status: status ?? "undefined" });
      captureMessage("Received unexpected billing status.");
    });

    return statusToTranslationMapping.geschlossen;
  }
};

export const assignFileToAnchor = (data: BillFile, elem: HTMLAnchorElement) => {
  if (data.billFile?.file && data.billFile.mimeType === "application/pdf") {
    const filename = `DR_Rechnung-${new Date()
      .toISOString()
      .replaceAll(/:|\./g, "-")}.pdf`;
    elem.setAttribute("target", "_blank");
    elem.setAttribute("download", filename);

    // On chrome, we cannot open data urls, so we convert to a blob and create it this way
    if (navigator.userAgent.includes("Firefox")) {
      elem.setAttribute(
        "href",
        `data:application/pdf;base64,${data.billFile?.file}`,
      );
    } else {
      // decode base64 string, remove space for IE compatibility
      const binary = atob(
        (data.billFile.file as unknown as string).replace(/\s/g, ""),
      );
      const len = binary.length;
      const buffer = new ArrayBuffer(len);
      const view = new Uint8Array(buffer);
      for (let i = 0; i < len; i++) {
        view[i] = binary.charCodeAt(i);
      }

      // create the blob object with content-type "application/pdf"
      const blob = new Blob([view], { type: "application/pdf" });
      elem.setAttribute("href", window.URL.createObjectURL(blob));
    }

    return elem;
  } else {
    log("Could not save bill pdf, the check for valid object failed.");
  }
};

export interface BillListRowProps {
  bill: DrBill;
  variant?: "default" | "compact";
  isSelectMultiple?: boolean;
}

export const BillListRow = ({
  bill,
  variant = "default",
  isSelectMultiple,
}: BillListRowProps) => {
  const {
    id,
    invoiceDate: date,
    custom1: name,
    billNumber,
    amount,
    status,
  } = bill;
  const { t } = useTranslation("portal");

  const [isLoading, setIsLoading] = useState(false);
  const handleError = useHandleError();

  const downloadBill = () => {
    setIsLoading(true);
    if (id) {
      getBillDocument(id)
        .then((data) => {
          let elem: HTMLAnchorElement | undefined =
            window.document.createElement("a");
          elem = assignFileToAnchor(data, elem);
          if (elem) {
            elem.click();
          }
        })
        .catch((error) => handleError(error))
        .finally(() => setIsLoading(false));
    }
  };

  if (!id) {
    return null;
  }

  const statusChip = status ? (
    <BillStatusChip
      status={status}
      label={t(`bills.list.status.${getValidStatus(status)}`)}
    />
  ) : null;

  if (variant === "compact") {
    // Mobile row: the left column is the tariff name over a single line holding
    // date, invoice number, status chip and — right-aligned — the amount. A
    // trailing column holds the open button, which the row checkbox takes over
    // while selecting.
    return (
      <>
        {isLoading && <LoadingSpinner />}
        <div data-testid="mobile-bill-list-row">
          <div className="flex h-16 items-center border-b border-border-tertiary">
            <div className="flex min-w-0 flex-1 flex-col gap-1 px-2">
              <TruncatedText
                className="text-sm font-semibold leading-md tracking-[0.28px] text-foreground-primary"
                text={name ?? ""}
              />
              {/* Date, status chip and amount on one line — the invoice
                  number only appears in the desktop table. Date/chip are
                  short and the row is wide, so however this leftover space
                  is handled, it shows up somewhere: packing everything left
                  (no spacers) put it all after the amount, right before the
                  button, which read as a wide gap between the amount and the
                  checkbox/open column; pushing the amount flush against that
                  column instead (`ml-auto` on just the amount) only moved
                  the same gap to before the amount, between the chip and the
                  sum. Splitting it across two spacers — one before and one
                  after the amount — halves however big that gap is instead
                  of concentrating it in either single spot. */}
              <div className="flex items-center justify-start text-xs tracking-tight text-foreground-primary">
                <span className="flex-1">
                  {date && getShortDateString(new Date(date))}
                </span>
                {statusChip && <span className="flex-1">{statusChip}</span>}
                <span className="flex-1">
                  {/* getCurrency joins the code and value with a non-breaking
                      space (Intl.NumberFormat's de-CH output), which blocks
                      any wrap there even once this span is squeezed - so
                      "CHF 56.00" overflowed past its box onto the button
                      instead of breaking onto a second line. Swap it for a
                      normal space so text-wrap can break "CHF" / "56.00"
                      when there isn't room for both on one line. */}
                  {getCurrency(amount || 0, "CHF").replace(" ", " ")}
                </span>
              </div>
            </div>
            {/* Trailing column: a slot for the open button, another for the
                checkbox. Note the mobile open button uses the plain
                arrow-right glyph, not the desktop's arrow-square-out. */}
            {isSelectMultiple ? (
              <div className="flex w-10 shrink-0 justify-center">
                <BillListCheckbox id={id} />
              </div>
            ) : (
              <div className="flex w-12 shrink-0 justify-center">
                <Button
                  dataTestid="mobile-bill-list-row-download"
                  variant="secondary"
                  size="sm"
                  type="button"
                  loading={isLoading}
                  ariaLabel={t("bills.list.downloadBill")}
                  icon={<ArrowRight className="size-4" />}
                  onClick={downloadBill}
                />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {isLoading && <LoadingSpinner />}
      <TableRow>
        <td>{date && getShortDateString(new Date(date))}</td>
        <td>{name}</td>
        <td>{billNumber}</td>
        {/* // TODO: Insert dynamic currency id here */}
        <td>{getCurrency(amount || 0, "CHF")}</td>
        <td>{statusChip ?? "-"}</td>
        {/* The trailing column swaps: the checkbox takes the place of
            "Öffnen" while selecting, on both breakpoints. */}
        {isSelectMultiple ? (
          <td className="bill-selection">
            <BillListCheckbox id={id} />
          </td>
        ) : (
          <td>
            <Button
              dataTestid="bill-download-icon"
              variant="secondary"
              size="sm"
              type="button"
              loading={isLoading}
              ariaLabel={t("bills.list.downloadBill")}
              icon={<OpenArrow className="size-4" />}
              onClick={downloadBill}
            />
          </td>
        )}
      </TableRow>
    </>
  );
};
